import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface WMSLayer {
  id: string;
  name: string;
  url: string;
  params: {
    layers: string;
    format?: string;
    transparent?: boolean;
    version?: string;
  };
  category?: string;
  defaultVisible: boolean;
  order: number;
}

interface Category {
  id: string;
  name: string;
}

const WMSLayerManager = () => {
  const [layers, setLayers] = useState<WMSLayer[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    layers: '',
    format: 'image/png',
    transparent: true,
    version: '1.1.1',
    category: '',
    defaultVisible: false,
  });

  useEffect(() => {
    // Fetch categories
    const categoriesQuery = query(collection(db, 'layerCategories'), orderBy('order', 'asc'));
    const unsubscribeCategories = onSnapshot(categoriesQuery, (snapshot) => {
      const fetchedCategories: Category[] = [];
      snapshot.forEach((doc) => {
        fetchedCategories.push({ id: doc.id, ...doc.data() } as Category);
      });
      setCategories(fetchedCategories);
    });

    // Fetch layers
    const layersQuery = query(collection(db, 'globalWMSLayers'), orderBy('order', 'asc'));
    const unsubscribeLayers = onSnapshot(layersQuery, (snapshot) => {
      const fetchedLayers: WMSLayer[] = [];
      snapshot.forEach((doc) => {
        fetchedLayers.push({ id: doc.id, ...doc.data() } as WMSLayer);
      });
      setLayers(fetchedLayers);
    });

    return () => {
      unsubscribeCategories();
      unsubscribeLayers();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      layers: '',
      format: 'image/png',
      transparent: true,
      version: '1.1.1',
      category: '',
      defaultVisible: false,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.url.trim() || !formData.layers.trim()) {
      setError('Name, URL, and Layers are required');
      return;
    }

    try {
      setError('');
      const baseLayerData = {
        name: formData.name.trim(),
        url: formData.url.trim(),
        params: {
          layers: formData.layers.trim(),
          format: formData.format,
          transparent: formData.transparent,
          version: formData.version,
        },
        category: formData.category || null,
        defaultVisible: formData.defaultVisible,
        updatedAt: new Date(),
      };

      if (editingId) {
        // When editing, don't update the order field
        const layerRef = doc(db, 'globalWMSLayers', editingId);
        await updateDoc(layerRef, baseLayerData);
        setEditingId(null);
      } else {
        // When adding, include order field
        await addDoc(collection(db, 'globalWMSLayers'), {
          ...baseLayerData,
          order: layers.length,
          createdAt: new Date(),
        });
      }

      resetForm();
      setIsAdding(false);
    } catch (err: any) {
      setError(`Failed to save layer: ${err.message}`);
    }
  };

  const handleEdit = (layer: WMSLayer) => {
    setFormData({
      name: layer.name,
      url: layer.url,
      layers: layer.params.layers,
      format: layer.params.format || 'image/png',
      transparent: layer.params.transparent !== false,
      version: layer.params.version || '1.1.1',
      category: layer.category || '',
      defaultVisible: layer.defaultVisible,
    });
    setEditingId(layer.id);
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this layer?')) {
      return;
    }

    try {
      setError('');
      await deleteDoc(doc(db, 'globalWMSLayers', id));
    } catch (err: any) {
      setError(`Failed to delete layer: ${err.message}`);
    }
  };

  const handleMoveLayer = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = layers.findIndex((layer) => layer.id === id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === layers.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const currentLayer = layers[currentIndex];
    const otherLayer = layers[newIndex];

    try {
      setError('');
      await updateDoc(doc(db, 'globalWMSLayers', currentLayer.id), { order: newIndex });
      await updateDoc(doc(db, 'globalWMSLayers', otherLayer.id), { order: currentIndex });
    } catch (err: any) {
      setError(`Failed to reorder layers: ${err.message}`);
    }
  };

  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return 'Uncategorized';
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  return (
    <div className="wms-layer-manager">
      <div className="manager-header">
        <h3>WMS Layers</h3>
        <button
          className="btn-small btn-primary"
          onClick={() => {
            resetForm();
            setIsAdding(true);
            setEditingId(null);
          }}
          disabled={isAdding}
        >
          + Add WMS Layer
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAdding && (
        <form onSubmit={handleSubmit} className="wms-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Layer Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sentinel-2 RGB"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="form-input"
              >
                <option value="">Uncategorized</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label>WMS URL *</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/wms"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Layers Parameter *</label>
              <input
                type="text"
                value={formData.layers}
                onChange={(e) => setFormData({ ...formData, layers: e.target.value })}
                placeholder="layer_name"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Format</label>
              <select
                value={formData.format}
                onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                className="form-input"
              >
                <option value="image/png">image/png</option>
                <option value="image/jpeg">image/jpeg</option>
                <option value="image/gif">image/gif</option>
              </select>
            </div>

            <div className="form-group">
              <label>WMS Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                className="form-input"
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.transparent}
                  onChange={(e) => setFormData({ ...formData, transparent: e.target.checked })}
                />
                <span>Transparent</span>
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.defaultVisible}
                  onChange={(e) => setFormData({ ...formData, defaultVisible: e.target.checked })}
                />
                <span>Visible by Default</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-small btn-primary">
              {editingId ? 'Update Layer' : 'Add Layer'}
            </button>
            <button
              type="button"
              className="btn-small btn-secondary"
              onClick={() => {
                setIsAdding(false);
                setEditingId(null);
                resetForm();
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="layers-list">
        {layers.length === 0 ? (
          <p className="empty-message">No WMS layers yet. Add one to get started!</p>
        ) : (
          layers.map((layer, index) => (
            <div key={layer.id} className="layer-item-card">
              <div className="layer-card-content">
                <div className="layer-header">
                  <h4>{layer.name}</h4>
                  <span className="category-badge">{getCategoryName(layer.category)}</span>
                </div>
                <div className="layer-details">
                  <p>
                    <strong>URL:</strong> {layer.url}
                  </p>
                  <p>
                    <strong>Layers:</strong> {layer.params.layers}
                  </p>
                  <p>
                    <strong>Format:</strong> {layer.params.format}
                  </p>
                  <p>
                    <strong>Visible by default:</strong> {layer.defaultVisible ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
              <div className="layer-card-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleMoveLayer(layer.id, 'up')}
                  disabled={index === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleMoveLayer(layer.id, 'down')}
                  disabled={index === layers.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleEdit(layer)}
                  title="Edit"
                >
                  ✎
                </button>
                <button
                  className="btn-icon btn-danger"
                  onClick={() => handleDelete(layer.id)}
                  title="Delete"
                >
                  ×
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default WMSLayerManager;
